import type { CSSProperties, RefObject } from "react";
import cx from "ui/cx";
import type { InputProps, InputRef } from "ui/forms/Input";
import Input from "ui/forms/Input";
import useEscapeKey from "ui/hooks/useEscapeKey";
import IconClose from "ui/icons/IconClose";
import IconSearch from "ui/icons/IconSearch";
import styles from "./SearchInput.module.scss";

export interface SearchInputProps extends Omit<InputProps, "size"> {
  clearSearch: () => void;
  containerStyles?: CSSProperties;
  fullWidth?: boolean;
  isInHeader?: boolean;
  containerRef?: RefObject<InputRef | null>;
  searchQuery?: string;
  size?: "medium" | "mediumLarge" | "large";
}

export default function SearchInput({
  clearSearch,
  containerStyles,
  fullWidth,
  isInHeader,
  containerRef,
  searchQuery,
  size,
  ...inputProps
}: SearchInputProps) {
  const classNames = cx({
    styles,
    wrapper: true,
    fullWidth: !!fullWidth,
    transparentInput: !!inputProps.transparent,
    headerStyle: !!isInHeader,
  });

  useEscapeKey(() => {
    if (containerRef?.current === document.activeElement) {
      clearSearch();
      containerRef?.current?.blur();
    }
  });

  const searchValue = searchQuery ?? inputProps.value ?? inputProps.defaultValue;
  const placeholder = inputProps.placeholder ?? "Search";

  return (
    <div className={classNames} style={containerStyles}>
      <label className={styles.searchLabel}>
        <IconSearch className={styles.searchIcon} />
        <Input
          {...inputProps}
          aria-label={inputProps["aria-label"] ?? placeholder}
          value={searchQuery ?? inputProps.value}
          inputSize={size || inputProps.inputSize}
          placeholder={placeholder}
          ref={containerRef}
          className={styles.searchInput}
        />
      </label>
      {searchValue && <IconClose className={styles.clearIcon} onClick={clearSearch} />}
    </div>
  );
}
